import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { usePageContext } from '../context/PageContext';

const RecruiterManagement = () => {
  const { setPageTitle } = usePageContext();
  const [recruiters, setRecruiters] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [showPlacementsModal, setShowPlacementsModal] = useState(false);
  const [editingRecruiter, setEditingRecruiter] = useState(null);
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedRecruiters, setSelectedRecruiters] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    email: ''
  });
  const [bonusData, setBonusData] = useState({
    fromHour: '',
    toHour: '',
    ratePerHour: ''
  });
  const { hasPermission } = useAuth();

  useEffect(() => {
    setPageTitle('Recruiter Management');
    if (hasPermission('recruiter', 'read')) {
      fetchRecruiters();
    }
  }, [setPageTitle, hasPermission]);

  const fetchRecruiters = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/recruiter');
      setRecruiters(response.data);
    } catch (error) {
      toast.error('Error fetching recruiters');
      setRecruiters([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlacements = async (recruiterId) => {
    try {
      const response = await axios.get(`/api/recruiter/${recruiterId}/placements`);
      setPlacements(response.data);
    } catch (error) {
      toast.error('Error fetching placements');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      if (editingRecruiter) {
        await axios.put(`/api/recruiter/${editingRecruiter._id}`, formData);
        toast.success('Recruiter updated successfully');
      } else {
        await axios.post('/api/recruiter', formData);
        toast.success('Recruiter created successfully');
      }
      setShowModal(false);
      setEditingRecruiter(null);
      setFormData({ name: '', phoneNumber: '', email: '' });
      setSelectedRecruiters([]);
      fetchRecruiters();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving recruiter');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (recruiterId) => {
    try {
      await axios.delete(`/api/recruiter/${recruiterId}`);
      toast.success('Recruiter deleted successfully');
      fetchRecruiters();
    } catch (error) {
      toast.error('Error deleting recruiter');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(selectedRecruiters.map(id => axios.delete(`/api/recruiter/${id}`)));
      toast.success(`${selectedRecruiters.length} recruiters deleted successfully`);
      setSelectedRecruiters([]);
      setShowDeleteModal(false);
      fetchRecruiters();
    } catch (error) {
      toast.error('Error deleting recruiters');
    }
  };

  const handleBonusSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`/api/recruiter/${selectedRecruiter._id}/bonus`, bonusData);
      toast.success('Bonus added successfully');
      setShowBonusModal(false);
      setBonusData({ fromHour: '', toHour: '', ratePerHour: '' });
      fetchRecruiters();
    } catch (error) {
      toast.error('Error adding bonus');
    }
  };

  const handleEdit = (recruiter) => {
    setEditingRecruiter(recruiter);
    setFormData({
      name: recruiter.name,
      phoneNumber: recruiter.phoneNumber,
      email: recruiter.email
    });
    setShowModal(true);
  };

  const handleViewPlacements = (recruiter) => {
    setSelectedRecruiter(recruiter);
    fetchPlacements(recruiter._id);
    setShowPlacementsModal(true);
  };

  const handleAddBonus = (recruiter) => {
    setSelectedRecruiter(recruiter);
    setShowBonusModal(true);
  };

  if (!hasPermission('recruiter', 'read')) {
    return <div className="alert alert-danger">Access denied</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <p className="text-muted mb-0">Manage recruiters, track placements and bonus structures</p>
          {selectedRecruiters.length > 0 && (
            <div className="d-flex align-items-center gap-2 mt-2">
              <span className="badge bg-primary">{selectedRecruiters.length} selected</span>
              <button className="btn btn-sm btn-danger" onClick={() => setShowDeleteModal(true)}>
                <i className="fas fa-trash me-1"></i>Delete Selected
              </button>
            </div>
          )}
        </div>
        {hasPermission('recruiter', 'create') && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <i className="fas fa-plus"></i> Add Recruiter
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading recruiters...</p>
        </div>
      ) : recruiters.length === 0 ? (
        <div className="text-center py-5">
          <div style={{ fontSize: '4rem', opacity: 0.3 }}>👥</div>
          <h5 className="text-muted mt-3">No recruiters found</h5>
          <p className="text-muted">Add recruiters to track placements and manage bonus structures.</p>
          {hasPermission('recruiter', 'create') && (
            <button className="btn btn-primary mt-3" onClick={() => setShowModal(true)}>
              <i className="fas fa-plus me-2"></i>Add First Recruiter
            </button>
          )}
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRecruiters(recruiters.map(r => r._id));
                      } else {
                        setSelectedRecruiters([]);
                      }
                    }}
                    checked={selectedRecruiters.length === recruiters.length && recruiters.length > 0}
                  />
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Placements</th>
                <th>Total Earnings</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {recruiters.map(recruiter => (
                <tr key={recruiter._id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedRecruiters.includes(recruiter._id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRecruiters([...selectedRecruiters, recruiter._id]);
                        } else {
                          setSelectedRecruiters(selectedRecruiters.filter(id => id !== recruiter._id));
                        }
                      }}
                    />
                  </td>
                  <td>{recruiter.name}</td>
                  <td>{recruiter.email}</td>
                  <td>{recruiter.phoneNumber}</td>
                  <td>
                    <button 
                      className="btn btn-sm btn-link p-0"
                      onClick={() => handleViewPlacements(recruiter)}
                    >
                      {recruiter.placements || 0}
                    </button>
                  </td>
                  <td>${(recruiter.totalEarnings || 0).toFixed(2)}</td>
                  <td>
                    <span className={`badge ${recruiter.isActive ? 'bg-success' : 'bg-danger'}`}>
                      {recruiter.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      {hasPermission('recruiter', 'update') && (
                        <>
                          <button className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(recruiter)}>
                            <i className="fas fa-edit"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-success" onClick={() => handleAddBonus(recruiter)}>
                            <i className="fas fa-plus"></i>
                          </button>
                        </>
                      )}
                      {hasPermission('recruiter', 'delete') && (
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(recruiter._id)}>
                          <i className="fas fa-trash"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Recruiter Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingRecruiter ? 'Edit Recruiter' : 'Add Recruiter'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Phone Number</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={formData.phoneNumber}
                      onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        {editingRecruiter ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      editingRecruiter ? 'Update' : 'Create'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Bonus Modal */}
      {showBonusModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Bonus for {selectedRecruiter?.name}</h5>
                <button type="button" className="btn-close" onClick={() => setShowBonusModal(false)}></button>
              </div>
              <form onSubmit={handleBonusSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">From Hour</label>
                    <input
                      type="number"
                      className="form-control"
                      value={bonusData.fromHour}
                      onChange={(e) => setBonusData({...bonusData, fromHour: e.target.value})}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">To Hour</label>
                    <input
                      type="number"
                      className="form-control"
                      value={bonusData.toHour}
                      onChange={(e) => setBonusData({...bonusData, toHour: e.target.value})}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Rate Per Hour ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      value={bonusData.ratePerHour}
                      onChange={(e) => setBonusData({...bonusData, ratePerHour: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowBonusModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Add Bonus
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Placements Modal */}
      {showPlacementsModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Placements by {selectedRecruiter?.name}</h5>
                <button type="button" className="btn-close" onClick={() => setShowPlacementsModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Employee Name</th>
                        <th>Client</th>
                        <th>Joining Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {placements.map(placement => (
                        <tr key={placement._id}>
                          <td>{placement.name}</td>
                          <td>{placement.client}</td>
                          <td>{new Date(placement.joiningDate).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {selectedRecruiter?.bonuses?.length > 0 && (
                  <div className="mt-4">
                    <h6>Bonus Structure</h6>
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>From Hour</th>
                            <th>To Hour</th>
                            <th>Rate Per Hour</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedRecruiter.bonuses.map((bonus, index) => (
                            <tr key={index}>
                              <td>{bonus.fromHour}</td>
                              <td>{bonus.toHour}</td>
                              <td>${bonus.ratePerHour}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPlacementsModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="text-center">
                  <i className="fas fa-exclamation-triangle text-warning" style={{fontSize: '3rem'}}></i>
                  <h5 className="mt-3">Are you sure?</h5>
                  <p className="text-muted">
                    You are about to delete {selectedRecruiters.length} recruiter(s). This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleBulkDelete}>
                  <i className="fas fa-trash me-2"></i>Delete {selectedRecruiters.length} Recruiter(s)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecruiterManagement;